import {
  type CreateUploadSessionsParams,
  type DirectoryUploadPrepareRequest,
  type DirectoryUploadPrepareResponse,
  type DirectoryUploadOperationStatus,
  type DirectoryUploadSealRequest,
  type FamilyGroupingMode,
  type PreparedDirectoryUploadFile,
  type UploadItem,
  type UploadSessionResponse,
  parseDirectoryUploadOperationStatus,
} from "@/api/files";
import { BATCHED_UPLOAD_SIZE } from "@/views/drive/modals/uploadModal/consts";
import { calculateTransferProgress, toUploadRows } from "@/views/drive/modals/uploadModal/helpers";
import { type FileWithProgress } from "@/views/drive/modals/uploadModal/types";
import { getUploadFilename, type CollectedUploadFile } from "./types";

const MAX_CONCURRENT_FOLDER_GROUPS = 6;
const UPLOAD_FAILED_MESSAGE = "Upload failed";

const serverDetailOr = (error: unknown, fallback: string): string => {
  const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
  return typeof detail === "string" && detail.length > 0 ? detail : fallback;
};
const PREPARATION_FAILED_MESSAGE = "Upload preparation failed";
const UPLOAD_NOT_PERMITTED_MESSAGE = "Upload not permitted";
const DUPLICATE_FLAT_FILE_MESSAGE = "This file was selected more than once. Remove the duplicate and try again.";
const UPLOAD_FINALIZATION_FAILED_MESSAGE = "Upload finalization failed";
const MAX_SEAL_ATTEMPTS = 3;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

const parsePreparedDirectoryUploadFile = (value: unknown): PreparedDirectoryUploadFile | undefined => {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.relative_path) ||
    !isNonEmptyString(value.filename) ||
    !(value.content_type === null || typeof value.content_type === "string") ||
    typeof value.size !== "number" ||
    !Number.isFinite(value.size) ||
    !isNonEmptyString(value.parent_fse_id) ||
    typeof value.exists !== "boolean"
  ) {
    return undefined;
  }
  return {
    relative_path: value.relative_path,
    filename: value.filename,
    content_type: value.content_type,
    size: value.size,
    parent_fse_id: value.parent_fse_id,
    exists: value.exists,
  };
};

const parseDirectoryUploadPrepareResponse = (value: unknown): DirectoryUploadPrepareResponse | undefined => {
  if (!isRecord(value) || !isNonEmptyString(value.operation_id) || !Array.isArray(value.files)) return undefined;
  const files: PreparedDirectoryUploadFile[] = [];
  for (const file of value.files) {
    const parsedFile = parsePreparedDirectoryUploadFile(file);
    if (!parsedFile) return undefined;
    files.push(parsedFile);
  }
  return { operation_id: value.operation_id, files };
};

const parseUploadItem = (value: unknown): UploadItem | undefined => {
  if (!isRecord(value) || !isNonEmptyString(value.filename) || !isNonEmptyString(value.put_path)) {
    return undefined;
  }
  return { filename: value.filename, put_path: value.put_path };
};

const parseUploadSessionResponse = (value: unknown): UploadSessionResponse | undefined => {
  if (!isRecord(value) || !isNonEmptyString(value.tx_id) || !Array.isArray(value.uploads)) return undefined;
  const uploads: UploadItem[] = [];
  for (const upload of value.uploads) {
    const parsedUpload = parseUploadItem(upload);
    if (!parsedUpload) return undefined;
    uploads.push(parsedUpload);
  }
  return { tx_id: value.tx_id, uploads };
};

export interface UploadDestination {
  fseId: string;
  name: string;
  canUpload: boolean;
  forceDirectoryPreparation?: boolean;
}

export interface PreparedUploadFile extends CollectedUploadFile {
  readonly clientId: string;
  readonly filename: string;
  readonly contentType: string | null;
  readonly size: number;
  readonly parentFseId: string;
  readonly exists: boolean;
}

export interface UploadOperationInput {
  files: readonly CollectedUploadFile[];
  destination: UploadDestination;
  familyGroupingMode?: FamilyGroupingMode;
}

export interface UploadTransactionRegistration {
  readonly txId: string;
  readonly parentFseId: string;
  readonly files: readonly {
    readonly clientId: string;
    readonly filename: string;
  }[];
}

export interface UploadCoordinatorDependencies {
  prepare(request: DirectoryUploadPrepareRequest): Promise<DirectoryUploadPrepareResponse>;
  seal(request: DirectoryUploadSealRequest): Promise<DirectoryUploadOperationStatus>;
  createSessions(request: CreateUploadSessionsParams): Promise<UploadSessionResponse>;
  uploadFile(file: File, putPath: string, onProgress: (fraction: number) => void): Promise<void>;
  triggerIngestion(request: { tx_id: string; fse_id: string }): Promise<void>;
  confirmConflicts(files: readonly PreparedUploadFile[]): Promise<ReadonlySet<string>>;
  getRows(): readonly FileWithProgress[];
  registerTransaction(registration: UploadTransactionRegistration): void;
  updateRows(update: (rows: FileWithProgress[]) => FileWithProgress[]): void;
}

export interface UploadOperationResult {
  txIds: ReadonlySet<string>;
  failedClientIds: ReadonlySet<string>;
  operationId?: string;
  sealAcknowledged?: boolean;
}

export const groupFilesByParent = (files: readonly PreparedUploadFile[]): Map<string, PreparedUploadFile[]> => {
  const groups = new Map<string, PreparedUploadFile[]>();
  for (const file of files) {
    const group = groups.get(file.parentFseId);
    if (group) {
      group.push(file);
    } else {
      groups.set(file.parentFseId, [file]);
    }
  }
  return groups;
};

export const runWithConcurrency = async <TItem, TResult>(
  items: readonly TItem[],
  concurrency: number,
  worker: (item: TItem, index: number) => Promise<TResult>,
): Promise<PromiseSettledResult<TResult>[]> => {
  if (!Number.isInteger(concurrency) || concurrency < 1) {
    throw new RangeError("Concurrency must be a positive integer");
  }

  const results: PromiseSettledResult<TResult>[] = new Array(items.length);
  let nextIndex = 0;
  const runWorker = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      try {
        results[index] = { status: "fulfilled", value: await worker(items[index], index) };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, () => runWorker()));
  return results;
};

const batchesOf = <T>(items: readonly T[], size: number): T[][] => {
  const batches: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size));
  }
  return batches;
};

const updateClientRows = (
  dependencies: UploadCoordinatorDependencies,
  clientIds: ReadonlySet<string>,
  update: (row: FileWithProgress) => FileWithProgress,
): void => {
  dependencies.updateRows(rows => rows.map(row => (clientIds.has(row.clientId) ? update(row) : row)));
};

const markFailed = (
  files: readonly PreparedUploadFile[],
  failedClientIds: Set<string>,
  dependencies: UploadCoordinatorDependencies,
  error: string,
): void => {
  const requestedClientIds = new Set(files.map(file => file.clientId));
  const terminalClientIds = new Set(
    dependencies
      .getRows()
      .filter(row => requestedClientIds.has(row.clientId) && (row.status === "completed" || row.status === "error"))
      .map(row => row.clientId),
  );
  const failedIds = new Set([...requestedClientIds].filter(clientId => !terminalClientIds.has(clientId)));
  for (const clientId of failedIds) failedClientIds.add(clientId);
  updateClientRows(dependencies, failedIds, row =>
    row.status === "completed" || row.status === "error" ? row : { ...row, status: "error", error },
  );
};

const toPreparedFlatFiles = (
  files: readonly CollectedUploadFile[],
  rowsByRelativePath: ReadonlyMap<string, FileWithProgress>,
  parentFseId: string,
): PreparedUploadFile[] =>
  files.flatMap(file => {
    const row = rowsByRelativePath.get(file.relativePath);
    if (!row) return [];
    return [
      {
        ...file,
        clientId: row.clientId,
        filename: getUploadFilename(file),
        contentType: file.file.type || null,
        size: file.file.size,
        parentFseId,
        exists: false,
      },
    ];
  });

const toPreparedDirectoryFiles = (
  files: readonly CollectedUploadFile[],
  rowsByRelativePath: ReadonlyMap<string, FileWithProgress>,
  response: DirectoryUploadPrepareResponse,
  failedClientIds: Set<string>,
  dependencies: UploadCoordinatorDependencies,
): PreparedUploadFile[] => {
  const prepared: PreparedUploadFile[] = [];
  const expectedRelativePaths = new Set(files.map(file => file.relativePath));
  if (
    response.files.length !== files.length ||
    response.files.some(file => !expectedRelativePaths.has(file.relative_path))
  ) {
    markFailed(
      toPreparedFlatFiles(files, rowsByRelativePath, ""),
      failedClientIds,
      dependencies,
      PREPARATION_FAILED_MESSAGE,
    );
    return prepared;
  }
  const returnedFilesByRelativePath = new Map<string, DirectoryUploadPrepareResponse["files"]>();
  for (const returnedFile of response.files) {
    const returnedFiles = returnedFilesByRelativePath.get(returnedFile.relative_path) ?? [];
    returnedFiles.push(returnedFile);
    returnedFilesByRelativePath.set(returnedFile.relative_path, returnedFiles);
  }

  for (const file of files) {
    const row = rowsByRelativePath.get(file.relativePath);
    if (!row) continue;
    const contentType = file.file.type || null;
    const normalizedFilename = getUploadFilename(file);
    const returnedFiles = returnedFilesByRelativePath.get(file.relativePath) ?? [];
    if (returnedFiles.length !== 1) {
      markFailed(
        [
          {
            ...file,
            clientId: row.clientId,
            filename: normalizedFilename,
            contentType,
            size: file.file.size,
            parentFseId: "",
            exists: false,
          },
        ],
        failedClientIds,
        dependencies,
        PREPARATION_FAILED_MESSAGE,
      );
      continue;
    }
    const returnedFile = returnedFiles[0];
    if (
      returnedFile.filename !== normalizedFilename ||
      returnedFile.content_type !== contentType ||
      returnedFile.size !== file.file.size ||
      returnedFile.parent_fse_id.length === 0
    ) {
      const mismatched: PreparedUploadFile = {
        ...file,
        clientId: row.clientId,
        filename: normalizedFilename,
        contentType,
        size: file.file.size,
        parentFseId: returnedFile.parent_fse_id,
        exists: returnedFile.exists,
      };
      markFailed([mismatched], failedClientIds, dependencies, PREPARATION_FAILED_MESSAGE);
      continue;
    }
    prepared.push({
      ...file,
      clientId: row.clientId,
      filename: returnedFile.filename,
      contentType: returnedFile.content_type,
      size: returnedFile.size,
      parentFseId: returnedFile.parent_fse_id,
      exists: returnedFile.exists,
    });
  }
  return prepared;
};

const matchSessionUploads = (
  batch: readonly PreparedUploadFile[],
  session: UploadSessionResponse,
): ReadonlyMap<string, UploadSessionResponse["uploads"][number]> | undefined => {
  if (session.tx_id.length === 0 || session.uploads.length !== batch.length) return undefined;
  const expectedFilenames = new Set(batch.map(file => file.filename));
  if (expectedFilenames.size !== batch.length) return undefined;

  const uploadsByFilename = new Map<string, UploadSessionResponse["uploads"][number]>();
  for (const upload of session.uploads) {
    if (
      !expectedFilenames.has(upload.filename) ||
      uploadsByFilename.has(upload.filename) ||
      upload.put_path.length === 0
    ) {
      return undefined;
    }
    uploadsByFilename.set(upload.filename, upload);
  }
  return uploadsByFilename;
};

const processBatchWork = async (
  batch: readonly PreparedUploadFile[],
  parentFseId: string,
  operationId: string | undefined,
  dependencies: UploadCoordinatorDependencies,
  txIds: Set<string>,
  failedClientIds: Set<string>,
): Promise<void> => {
  let session: UploadSessionResponse;
  try {
    const response: unknown = await dependencies.createSessions({
      fse_id: parentFseId,
      files: batch.map(file => ({
        filename: file.filename,
        content_type: file.contentType,
        size: file.size,
      })),
      ...(operationId !== undefined ? { operation_id: operationId } : {}),
    });
    const parsedResponse = parseUploadSessionResponse(response);
    if (!parsedResponse) {
      markFailed(batch, failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
      return;
    }
    session = parsedResponse;
  } catch (error) {
    markFailed(batch, failedClientIds, dependencies, serverDetailOr(error, UPLOAD_FAILED_MESSAGE));
    return;
  }

  const uploadsByFilename = matchSessionUploads(batch, session);
  if (!uploadsByFilename) {
    markFailed(batch, failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
    return;
  }

  const batchClientIds = new Set(batch.map(file => file.clientId));
  const filenameByClientId = new Map(
    batch.map(file => [file.clientId, uploadsByFilename.get(file.filename)!.filename]),
  );
  updateClientRows(dependencies, batchClientIds, row => ({
    ...row,
    tx_id: session.tx_id,
    upload_filename: filenameByClientId.get(row.clientId),
  }));
  txIds.add(session.tx_id);
  try {
    dependencies.registerTransaction({
      txId: session.tx_id,
      parentFseId,
      files: batch.map(file => ({
        clientId: file.clientId,
        filename: uploadsByFilename.get(file.filename)!.filename,
      })),
    });
  } catch {
    markFailed(batch, failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
    return;
  }

  const uploadResults = await Promise.allSettled(
    batch.map(file =>
      dependencies.uploadFile(file.file, uploadsByFilename.get(file.filename)!.put_path, fraction => {
        const clientIds = new Set([file.clientId]);
        updateClientRows(dependencies, clientIds, row => ({
          ...row,
          status: "uploading",
          progress: Math.max(row.progress, calculateTransferProgress(fraction, file.file.size)),
        }));
      }),
    ),
  );

  const successful: PreparedUploadFile[] = [];
  uploadResults.forEach((result, index) => {
    if (result.status === "fulfilled") {
      successful.push(batch[index]);
    } else {
      markFailed([batch[index]], failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
    }
  });
  if (successful.length === 0) return;

  try {
    await dependencies.triggerIngestion({ tx_id: session.tx_id, fse_id: parentFseId });
  } catch {
    markFailed(successful, failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
    return;
  }

  const successfulClientIds = new Set(successful.map(file => file.clientId));
  updateClientRows(dependencies, successfulClientIds, row =>
    row.status === "completed" || row.status === "error" ? row : { ...row, status: "started" },
  );
};

const processBatch = async (
  batch: readonly PreparedUploadFile[],
  parentFseId: string,
  operationId: string | undefined,
  dependencies: UploadCoordinatorDependencies,
  txIds: Set<string>,
  failedClientIds: Set<string>,
): Promise<void> => {
  try {
    await processBatchWork(batch, parentFseId, operationId, dependencies, txIds, failedClientIds);
  } catch {
    markFailed(batch, failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
  }
};

export const runUploadOperation = async (
  input: UploadOperationInput,
  dependencies: UploadCoordinatorDependencies,
): Promise<UploadOperationResult> => {
  const txIds = new Set<string>();
  const failedClientIds = new Set<string>();
  const seenFlatNames = new Set<string>();
  const duplicateFlatIndexes = new Set<number>();
  input.files.forEach((file, index) => {
    if (file.directoryPath.length > 0) return;
    const filename = getUploadFilename(file);
    if (seenFlatNames.has(filename)) {
      duplicateFlatIndexes.add(index);
      return;
    }
    seenFlatNames.add(filename);
  });
  const initialRows = toUploadRows(input.files).map((row, index) =>
    duplicateFlatIndexes.has(index) ? { ...row, status: "error" as const, error: DUPLICATE_FLAT_FILE_MESSAGE } : row,
  );
  dependencies.updateRows(rows => [...rows, ...initialRows]);
  for (const row of initialRows) {
    if (row.status === "error") failedClientIds.add(row.clientId);
  }

  const rowsByRelativePath = new Map<string, FileWithProgress>();
  initialRows.forEach(row => {
    if (row.status === "pending" && !rowsByRelativePath.has(row.relativePath)) {
      rowsByRelativePath.set(row.relativePath, row);
    }
  });
  const validFiles = input.files.filter(
    (_file, index) => initialRows[index].status === "pending" && !duplicateFlatIndexes.has(index),
  );
  if (validFiles.length === 0) return { txIds, failedClientIds };

  if (!input.destination.canUpload) {
    const prepared = toPreparedFlatFiles(validFiles, rowsByRelativePath, input.destination.fseId);
    markFailed(prepared, failedClientIds, dependencies, UPLOAD_NOT_PERMITTED_MESSAGE);
    return { txIds, failedClientIds };
  }

  const requiresPreparation =
    input.destination.forceDirectoryPreparation === true || validFiles.some(file => file.directoryPath.length > 0);
  const skippedConflictPaths = new Set<string>();
  const sealPreparedOperation = async (operationId: string): Promise<boolean> => {
    const failedPathSet = new Set(
      initialRows
        .filter(row => failedClientIds.has(row.clientId) && !skippedConflictPaths.has(row.relativePath))
        .map(row => row.relativePath),
    );
    for (let attempt = 0; attempt < MAX_SEAL_ATTEMPTS; attempt += 1) {
      try {
        const response: unknown = await dependencies.seal({
          operation_id: operationId,
          skipped_conflict_paths: [...skippedConflictPaths],
          failed_paths: [...failedPathSet],
        });
        parseDirectoryUploadOperationStatus(response, operationId);
        return true;
      } catch {
        if (attempt + 1 === MAX_SEAL_ATTEMPTS) break;
      }
    }
    const includedFiles = toPreparedFlatFiles(
      validFiles.filter(file => !skippedConflictPaths.has(file.relativePath)),
      rowsByRelativePath,
      "",
    );
    markFailed(includedFiles, failedClientIds, dependencies, UPLOAD_FINALIZATION_FAILED_MESSAGE);
    return false;
  };

  const processPreparedFiles = async (
    initialPreparedFiles: PreparedUploadFile[],
    operationId: string | undefined,
  ): Promise<void> => {
    let preparedFiles = initialPreparedFiles;
    const conflicts = preparedFiles.filter(file => file.exists);
    if (conflicts.length > 0) {
      let confirmedPaths: ReadonlySet<string> | undefined;
      try {
        confirmedPaths = await dependencies.confirmConflicts(conflicts);
      } catch {
        markFailed(conflicts, failedClientIds, dependencies, UPLOAD_FAILED_MESSAGE);
        preparedFiles = preparedFiles.filter(file => !file.exists);
      }
      if (confirmedPaths) {
        const cancelledClientIds = new Set(
          conflicts.filter(file => !confirmedPaths.has(file.relativePath)).map(file => file.clientId),
        );
        for (const conflict of conflicts) {
          if (!confirmedPaths.has(conflict.relativePath)) skippedConflictPaths.add(conflict.relativePath);
        }
        if (cancelledClientIds.size > 0) {
          dependencies.updateRows(rows => rows.filter(row => !cancelledClientIds.has(row.clientId)));
        }
        preparedFiles = preparedFiles.filter(file => !file.exists || confirmedPaths.has(file.relativePath));
      }
    }

    const groups = [...groupFilesByParent(preparedFiles).entries()];
    await runWithConcurrency(groups, MAX_CONCURRENT_FOLDER_GROUPS, async ([parentFseId, group]) => {
      for (const batch of batchesOf(group, BATCHED_UPLOAD_SIZE)) {
        await processBatch(batch, parentFseId, operationId, dependencies, txIds, failedClientIds);
      }
    });
  };

  if (!requiresPreparation) {
    await processPreparedFiles(toPreparedFlatFiles(validFiles, rowsByRelativePath, input.destination.fseId), undefined);
    return { txIds, failedClientIds };
  }

  let parsedResponse: DirectoryUploadPrepareResponse;
  try {
    const response: unknown = await dependencies.prepare({
      fse_id: input.destination.fseId,
      files: validFiles.map(file => ({
        relative_path: file.relativePath,
        content_type: file.file.type || null,
        size: file.file.size,
      })),
      family_grouping_mode: input.familyGroupingMode ?? "none",
    });
    const parsed = parseDirectoryUploadPrepareResponse(response);
    if (!parsed) throw new Error(PREPARATION_FAILED_MESSAGE);
    parsedResponse = parsed;
  } catch (error) {
    const prepared = toPreparedFlatFiles(validFiles, rowsByRelativePath, input.destination.fseId);
    markFailed(prepared, failedClientIds, dependencies, serverDetailOr(error, PREPARATION_FAILED_MESSAGE));
    return { txIds, failedClientIds };
  }

  const operationId = parsedResponse.operation_id;
  let sealAcknowledged = false;
  try {
    const preparedFiles = toPreparedDirectoryFiles(
      validFiles,
      rowsByRelativePath,
      parsedResponse,
      failedClientIds,
      dependencies,
    );
    await processPreparedFiles(preparedFiles, operationId);
  } finally {
    sealAcknowledged = await sealPreparedOperation(operationId);
  }

  return { txIds, failedClientIds, operationId, sealAcknowledged };
};
